import React, { useState, useRef, useMemo } from 'react';
import { Plus, X, ChevronDown, ChevronUp, AlertCircle, Trash2, Edit2, Sparkles } from 'lucide-react';
import { Question } from '../../../api/jobs';
import { cn } from '../../../utils/cn';
import { useLanguage } from '../../../contexts/LanguageContext';
import { aiApi } from '../../../api/ai';

interface QuestionGroup {
  name: string;
  weight: number;
  isActive: boolean;
}

interface JobData {
  job_title: string;
  department: string;
  description: string;
}

interface ReferenceConfigurationProps {
  questions: Question[];
  onChange: (questions: Question[], groups: QuestionGroup[]) => void;
  jobData: JobData;
}

const getDefaultGroups = (t: any): QuestionGroup[] => [
  { name: t('dashboard.newJobModal.referenceConfig.categories.behaviourSkills'), weight: 25, isActive: true },
  { name: t('dashboard.newJobModal.referenceConfig.categories.communication'), weight: 25, isActive: true },
  { name: t('dashboard.newJobModal.referenceConfig.categories.problemSolving'), weight: 25, isActive: true },
  { name: t('dashboard.newJobModal.referenceConfig.categories.technicalSkills'), weight: 25, isActive: true }
];

const ReferenceConfiguration = ({ questions, onChange, jobData }: ReferenceConfigurationProps) => {
  const { t } = useLanguage();
  const defaultGroups = useMemo(() => getDefaultGroups(t), [t]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(defaultGroups.map(g => g.name)));
  const [groups, setGroups] = useState<QuestionGroup[]>(defaultGroups);
  const [editingGroupName, setEditingGroupName] = useState<string | null>(null);
  const groupNameInputRef = useRef<HTMLInputElement>(null);
  const [generatingQuestions, setGeneratingQuestions] = useState<Record<string, boolean>>({});

  const toggleGroup = (groupName: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const handleAddQuestion = (group: string) => {
    const newQuestions = [
      ...questions,
      {
        label: '',
        group,
        is_active: true,
        sort_order: questions.length
      }
    ];
    onChange(newQuestions, groups);
  };

  const handleCreateWithAI = async (group: string) => {
    // Check if job title, department, and description are available
    if (!jobData.job_title || !jobData.department || !jobData.description) {
      alert(t('dashboard.newJobModal.referenceConfig.aiNeedsJobInfo'));
      return;
    }

    // Mark as generating for this group
    setGeneratingQuestions(prev => ({ ...prev, [group]: true }));

    try {
      // Get existing questions for this category
      const existingQuestions = getGroupQuestions(group).map(q => q.label).filter(label => label.trim() !== '');

      // Call the AI endpoint
      const response = await aiApi.createQuestion({
        job_title: jobData.job_title,
        department: jobData.department,
        job_description: jobData.description,
        question_category: group,
        existing_category_questions: existingQuestions
      });

      if (response && response.suggested_question) {
        // Add the generated question to the list
        const newQuestions = [
          ...questions,
          {
            label: response.suggested_question,
            // Use the returned category if it exists and matches a group, otherwise use the requested group
            group: response.question_category && groups.some(g => g.name === response.question_category) 
              ? response.question_category 
              : group,
            is_active: true,
            sort_order: questions.length
          }
        ];
        onChange(newQuestions, groups);
      }
    } catch (error) {
      console.error('Failed to generate question:', error);
      alert(t('dashboard.newJobModal.referenceConfig.aiGenerationError'));
    } finally {
      // Mark as no longer generating
      setGeneratingQuestions(prev => ({ ...prev, [group]: false }));
    }
  };

  const handleRemoveQuestion = (index: number) => {
    const newQuestions = questions.filter((_, i) => i !== index);
    onChange(newQuestions, groups);
  };

  const handleQuestionChange = (index: number, field: keyof Question, value: any) => {
    const newQuestions = [...questions];
    newQuestions[index] = {
      ...newQuestions[index],
      [field]: value
    };
    onChange(newQuestions, groups);
  };

  const handleGroupWeightChange = (groupName: string, weight: number) => {
    const newGroups = groups.map(g => 
      g.name === groupName ? { ...g, weight } : g
    );
    setGroups(newGroups);
    onChange(questions, newGroups);
  };

  const handleRemoveGroup = (groupName: string) => {
    // Remove group from active groups
    const newGroups = groups.filter(g => g.name !== groupName);
    setGroups(newGroups);
    
    // Remove all questions from this group
    const newQuestions = questions.filter(q => q.group !== groupName);
    onChange(newQuestions, newGroups);
    
    // Remove from expanded groups
    const newExpanded = new Set(expandedGroups);
    newExpanded.delete(groupName);
    setExpandedGroups(newExpanded);
  };
  
  const handleAddGroup = () => {
    const baseName = t('dashboard.newJobModal.referenceConfig.categories.newCategory');
    // Create unique name if "New Category" already exists
    let newGroupName = baseName;
    let counter = 1;
    
    while (groups.some(g => g.name === newGroupName)) {
      newGroupName = `${baseName} ${counter}`;
      counter++;
    }
    
    const newGroup: QuestionGroup = {
      name: newGroupName,
      weight: 0,
      isActive: true
    };
    
    const newGroups = [...groups, newGroup];
    setGroups(newGroups);
    onChange(questions, newGroups);
    
    // Expand new group
    const newExpanded = new Set(expandedGroups);
    newExpanded.add(newGroupName);
    setExpandedGroups(newExpanded);
    
    // Start editing the name immediately
    setEditingGroupName(newGroupName);
    // Focus will be set in useEffect
  };
  
  const handleEditGroupName = (oldName: string) => {
    setEditingGroupName(oldName);
    // Focus will happen after render via useEffect
    setTimeout(() => {
      if (groupNameInputRef.current) {
        groupNameInputRef.current.focus();
      }
    }, 0);
  };
  
  const handleSaveGroupName = (oldName: string, newName: string) => {
    if (!newName.trim()) return; // Don't save empty names
    
    // Check if the new name already exists
    if (oldName !== newName && groups.some(g => g.name === newName)) {
      alert("A group with this name already exists.");
      return;
    }
    
    // Update group name
    const newGroups = groups.map(g => 
      g.name === oldName ? { ...g, name: newName } : g
    );
    setGroups(newGroups);
    
    // Update questions with this group
    const newQuestions = questions.map(q => 
      q.group === oldName ? { ...q, group: newName } : q
    );
    
    onChange(newQuestions, newGroups);
    
    // Update expanded groups
    if (oldName !== newName) {
      const newExpanded = new Set(expandedGroups);
      if (newExpanded.has(oldName)) {
        newExpanded.delete(oldName);
        newExpanded.add(newName);
      }
      setExpandedGroups(newExpanded);
    }
    
    setEditingGroupName(null);
  };

  const getGroupQuestions = (group: string) => {
    return questions.filter(q => q.group === group);
  };

  // Calculate total weight from all groups
  const totalWeight = groups.reduce((sum, group) => sum + group.weight, 0);

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium text-gray-900">{t('dashboard.newJobModal.referenceConfig.questionGroups')}</h3>
          <button
            type="button"
            onClick={handleAddGroup}
            className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1.5 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            {t('dashboard.newJobModal.referenceConfig.addCategory')}
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {t('dashboard.newJobModal.referenceConfig.groupDescription')}
        </p>

        {/* Weight Status Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">{t('dashboard.newJobModal.referenceConfig.totalWeight')}</span>
              <span className={cn(
                "text-sm font-bold",
                totalWeight === 100 ? "text-green-600" : "text-red-600"
              )}>
                {totalWeight}%
              </span>
            </div>
            <span className="text-sm font-medium text-gray-500">{t('dashboard.newJobModal.referenceConfig.target')}</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={cn(
                "h-full rounded-full transition-all duration-300",
                totalWeight === 100 ? "bg-green-500" : "bg-red-500"
              )}
              style={{ width: `${Math.min(totalWeight, 100)}%` }}
            />
          </div>
          
          {totalWeight !== 100 && (
            <div className="mt-3 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-600">
                  {totalWeight > 100 ? t('dashboard.newJobModal.referenceConfig.weightExceeds') : t('dashboard.newJobModal.referenceConfig.weightLess')}
                </p>
                <p className="text-sm text-red-600 mt-1">
                  {t('dashboard.newJobModal.referenceConfig.adjustWeights')}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {groups.map((group) => (
            <div key={group.name} className="bg-white rounded-lg border overflow-hidden">
              <div className="p-4 bg-gray-50 border-b flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {editingGroupName === group.name ? (
                        <div 
                          className="flex items-center"
                        >
                          <input
                            ref={groupNameInputRef}
                            type="text"
                            defaultValue={group.name}
                            className="py-1 px-2 border rounded text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-500 w-48"
                            onBlur={(e) => handleSaveGroupName(group.name, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveGroupName(group.name, e.target.value);
                              } else if (e.key === 'Escape') {
                                setEditingGroupName(null);
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900">{group.name}</h4>
                          <button
                            type="button"
                            onClick={() => handleEditGroupName(group.name)}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title={t('dashboard.newJobModal.referenceConfig.editName')}
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      {getGroupQuestions(group.name).length === 0 && (
                        <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full">
                          {t('dashboard.newJobModal.referenceConfig.noQuestions')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">{t('dashboard.newJobModal.referenceConfig.weight')}</label>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={group.weight}
                            onChange={(e) => handleGroupWeightChange(group.name, parseInt(e.target.value) || 0)}
                            className="w-20 px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm pr-8"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">%</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveGroup(group.name)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('dashboard.newJobModal.referenceConfig.removeGroup')}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.name)}
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        {expandedGroups.has(group.name) ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="absolute inset-0 bg-blue-600 rounded-full transition-all duration-300"
                      style={{ width: `${group.weight}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className={cn(
                "transition-all duration-300",
                !expandedGroups.has(group.name) && "hidden"
              )}>
                <div className="p-4 space-y-4">
                  {getGroupQuestions(group.name).map((question, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <textarea
                            value={question.label}
                            onChange={(e) => handleQuestionChange(
                              questions.findIndex(q => q === question),
                              'label',
                              e.target.value
                            )}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 mb-2 min-h-[80px] resize-y"
                            placeholder={t('dashboard.newJobModal.referenceConfig.questionPlaceholder', { category: group.name.toLowerCase() })}
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={question.is_active}
                              onChange={(e) => handleQuestionChange(
                                questions.findIndex(q => q === question),
                                'is_active',
                                e.target.checked
                              )}
                              className="rounded text-blue-600 focus:ring-blue-500"
                            />
                            <label className="text-sm text-gray-700">{t('dashboard.newJobModal.referenceConfig.active')}</label>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveQuestion(questions.findIndex(q => q === question))}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleAddQuestion(group.name)}
                      className="flex-1 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      {t('dashboard.newJobModal.referenceConfig.addQuestion')}
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => handleCreateWithAI(group.name)}
                      disabled={generatingQuestions[group.name]}
                      className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {generatingQuestions[group.name] ? (
                        <span className="h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"/>
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                      {t('dashboard.newJobModal.referenceConfig.aiCreateQuestion')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReferenceConfiguration;